import React, { useEffect, useRef, useCallback } from "react";

interface RainBackgroundProps {
  className?: string;
  dropCount?: number;
  lightning?: boolean;
  color?: string;
  deflectRadius?: number;
  deflectStrength?: number;
}

interface Raindrop {
  x: number;
  y: number;
  length: number;
  speed: number;
  opacity: number;
  width: number;
}

interface SplashParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
}

const RainBackground: React.FC<RainBackgroundProps> = ({
  className = "",
  dropCount = 120,
  lightning = true,
  color = "rgba(140, 200, 255, 0.6)",
  deflectRadius = 60,
  deflectStrength = 0.15,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const flashRef = useRef(0);
  const nextFlashRef = useRef(Math.random() * 8000 + 4000);
  const timeRef = useRef(0);
  const mouseRef = useRef<{ x: number; y: number } | null>(null);
  const splashesRef = useRef<SplashParticle[]>([]);

  const createDrops = useCallback(
    (w: number, h: number): Raindrop[][] => {
      const layers: Raindrop[][] = [];
      const configs = [
        { count: Math.floor(dropCount * 0.5), speedMin: 4, speedMax: 8, lenMin: 14, lenMax: 24, opMin: 0.15, opMax: 0.35, widthMax: 0.8 },
        { count: Math.floor(dropCount * 0.35), speedMin: 9, speedMax: 14, lenMin: 18, lenMax: 30, opMin: 0.3, opMax: 0.55, widthMax: 1.2 },
        { count: Math.floor(dropCount * 0.15), speedMin: 15, speedMax: 22, lenMin: 25, lenMax: 40, opMin: 0.5, opMax: 0.75, widthMax: 1.8 },
      ];
      for (const cfg of configs) {
        const drops: Raindrop[] = [];
        for (let i = 0; i < cfg.count; i++) {
          drops.push({
            x: Math.random() * w,
            y: Math.random() * h * -1 - 10,
            length: cfg.lenMin + Math.random() * (cfg.lenMax - cfg.lenMin),
            speed: cfg.speedMin + Math.random() * (cfg.speedMax - cfg.speedMin),
            opacity: cfg.opMin + Math.random() * (cfg.opMax - cfg.opMin),
            width: 0.4 + Math.random() * cfg.widthMax,
          });
        }
        layers.push(drops);
      }
      return layers;
    },
    [dropCount]
  );

  const spawnSplash = useCallback((x: number, y: number, force: number) => {
    const splashes = splashesRef.current;
    // Limit total splash particles for performance
    if (splashes.length > 80) return;
    const count = Math.floor(2 + force * 4);
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = (1 + Math.random() * 3) * (0.5 + force);
      splashes.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - Math.random() * 1.5,
        life: 1,
        maxLife: 200 + Math.random() * 200,
        size: 1 + Math.random() * 2 * force,
      });
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let w = canvas.parentElement?.clientWidth || canvas.clientWidth;
    let h = canvas.parentElement?.clientHeight || canvas.clientHeight;
    canvas.width = w;
    canvas.height = h;

    let layers = createDrops(w, h);
    let lastTime = performance.now();

    const container = canvas.parentElement;

    const handleMouseMove = (e: MouseEvent) => {
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (x >= -50 && x <= rect.width + 50 && y >= -50 && y <= rect.height + 50) {
        mouseRef.current = { x, y };
      } else {
        mouseRef.current = null;
      }
    };
    const handleMouseLeave = () => {
      mouseRef.current = null;
    };

    window.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseleave", handleMouseLeave);

    const resize = () => {
      w = canvas.parentElement?.clientWidth || canvas.clientWidth;
      h = canvas.parentElement?.clientHeight || canvas.clientHeight;
      canvas.width = w;
      canvas.height = h;
      layers = createDrops(w, h);
    };

    const draw = (now: number) => {
      const dt = Math.min(now - lastTime, 50);
      lastTime = now;
      timeRef.current += dt;

      ctx.clearRect(0, 0, w, h);

      // Lightning flash
      if (lightning) {
        if (flashRef.current > 0) {
          flashRef.current -= dt;
          const intensity = Math.max(0, flashRef.current / 150);
          ctx.fillStyle = `rgba(200, 220, 255, ${intensity * 0.12})`;
          ctx.fillRect(0, 0, w, h);
        }
        nextFlashRef.current -= dt;
        if (nextFlashRef.current <= 0) {
          flashRef.current = 100 + Math.random() * 100;
          nextFlashRef.current = Math.random() * 12000 + 5000;
        }
      }

      const angle = 0.25;
      const mouse = mouseRef.current;
      const r2 = deflectRadius * deflectRadius;
      const innerRadius = deflectRadius * 0.3;

      for (const drops of layers) {
        for (const drop of drops) {
          // Move drop
          drop.y += drop.speed * (dt / 16);
          drop.x += Math.sin(angle) * drop.speed * (dt / 16) * 0.3;

          // Mouse deflection
          let opacityBoost = 0;
          let widthBoost = 0;
          let lengthBoost = 0;
          if (mouse) {
            const dx = drop.x - mouse.x;
            const dy = drop.y - mouse.y;
            const dist2 = dx * dx + dy * dy;
            if (dist2 < r2 && dist2 > 0) {
              const dist = Math.sqrt(dist2);
              const normalizedForce = 1 - dist / deflectRadius;
              const force = normalizedForce * normalizedForce * normalizedForce;
              const nx = dx / dist;
              const ny = dy / dist;
              const pushMag = force * deflectStrength * (drop.speed + 8);
              drop.x += nx * pushMag;
              drop.y += ny * pushMag * 0.25;
              opacityBoost = force * 0.5 + 0.15;
              widthBoost = force * 1.5;
              lengthBoost = force * 12;

              // Spawn splash when drop hits the deflection boundary
              if (dist < innerRadius && force > 0.15) {
                // Splash at the edge of the shield
                const edgeX = mouse.x + nx * deflectRadius * 0.4;
                const edgeY = mouse.y + ny * deflectRadius * 0.4;
                spawnSplash(edgeX, edgeY, force);
              }

              // Teleport very close drops to edge
              if (dist < deflectRadius * 0.15) {
                const edgeDist = deflectRadius * 0.5;
                drop.x = mouse.x + nx * edgeDist;
                drop.y = mouse.y + ny * edgeDist;
              }
            }
          }

          // Draw raindrop
          ctx.beginPath();
          ctx.strokeStyle = color;
          ctx.globalAlpha = Math.min(1, drop.opacity + opacityBoost);
          ctx.lineWidth = drop.width + widthBoost;
          const drawLength = drop.length + lengthBoost;
          const ddx = Math.sin(angle) * drawLength;
          const ddy = Math.cos(angle) * drawLength;
          ctx.moveTo(drop.x, drop.y);
          ctx.lineTo(drop.x + ddx, drop.y + ddy);
          ctx.stroke();

          // Reset when off screen
          if (drop.y > h + 10 || drop.x > w + 50 || drop.x < -50) {
            drop.y = -drop.length - Math.random() * 40;
            drop.x = Math.random() * w;
          }
        }
      }

      // Draw & update splash particles
      const splashes = splashesRef.current;
      for (let i = splashes.length - 1; i >= 0; i--) {
        const sp = splashes[i];
        sp.life -= dt / sp.maxLife;
        if (sp.life <= 0) {
          splashes.splice(i, 1);
          continue;
        }
        sp.x += sp.vx * (dt / 16);
        sp.y += sp.vy * (dt / 16);
        sp.vy += 0.08 * (dt / 16); // gravity

        const alpha = sp.life * 0.7;
        ctx.beginPath();
        ctx.fillStyle = color;
        ctx.globalAlpha = alpha;
        ctx.arc(sp.x, sp.y, sp.size * sp.life, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    window.addEventListener("resize", resize);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [createDrops, lightning, color, deflectRadius, deflectStrength, spawnSplash]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 ${className}`}
      style={{ pointerEvents: "none" }}
    />
  );
};

export default RainBackground;